import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnLabel from "@shared/components/SnLabel";
import SnRadioBox from "@shared/components/SnRadioBox";
import SnTab from "@shared/components/SnTab";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { showDialog } from "@widgets/CommonDialog";
import { IconButton } from "@mui/material";

import ArrowCircleUpTwoToneIcon from "@mui/icons-material/ArrowCircleUpTwoTone";
import ArrowCircleDownTwoToneIcon from "@mui/icons-material/ArrowCircleDownTwoTone";
import ArrowCircleRightTwoToneIcon from "@mui/icons-material/ArrowCircleRightTwoTone";
import ArrowCircleLeftTwoToneIcon from "@mui/icons-material/ArrowCircleLeftTwoTone";

interface PopupProps {
  modalRef?: any;
  cd_menu?: string;
  id_target?: string;
  columnInfos: any[];
}

interface RowData {
  [key: string]: any;
}

// 데이터타입 값을 대문자 표준형으로 변환한다.
const normalizeTpData = (value: any) =>
  String(value ?? "")
    .trim()
    .toUpperCase();
const displayFgOptions = [
  { code: "G", name: "Grid" },
  { code: "F", name: "Freeform" },
];

const SYS_ADDINFO_POP_01 = forwardRef(
  ({ cd_menu, id_target, columnInfos }: PopupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const grid1 = useRef<any>(null);
    const gridTarget = useRef<any>(null);
    const gridDisplay = useRef<any>(null);
    const pendingDisplayFocusKeyRef = useRef<string>("");
    const popupRootRef = useRef<HTMLDivElement>(null);
    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );

    const [columns, setColumns] = useState<any[]>([]);
    const [targetColumnsConfig, setTargetColumnsConfig] = useState<any[]>([]);
    const [displayColumnsConfig, setDisplayColumnsConfig] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any[]>([]);
    const [displayFg, setDisplayFg] = useState<"G" | "F">("G");
    const [masterColumns, setMasterColumns] = useState<any[]>([]);
    const [targetColumns, setTargetColumns] = useState<any[]>([]);
    const [displayColumns, setDisplayColumns] = useState<any[]>([]);
    const [configChanged, setConfigChanged] = useState<boolean>(false);
    const [hasDisplayConfig, setHasDisplayConfig] = useState<boolean>(false);
    const [initialDisplayRows, setInitialDisplayRows] = useState<any[]>([]);

    const splitCdMenu = "SYS_ADDINFO_POP_01";
    const splitDefaultSize = { panel1height: 330, panel2height: "100%" };
    const [splitSizes, onSplitResizeStop] = usePanelSizes(
      splitCdMenu,
      splitDefaultSize
    );

    // 표시항목 순번을 현재 배열 순서로 다시 부여한다.
    const resequenceDisplayColumns = (rows: any[]) =>
      rows.map((row: any, idx: number) => ({
        ...row,
        no_seq_p: idx + 1,
      }));

    // 대상/표시항목 리스트를 초기화한다.
    const initializeColumnLists = (sourceColumns: any[]) => {
      const initialMaster = Array.isArray(sourceColumns) ? sourceColumns : [];
      setMasterColumns(initialMaster);
      setTargetColumns(initialMaster);
      setDisplayColumns([]);
      setDisplayFg("G");
      setConfigChanged(false);
      setHasDisplayConfig(false);
      setInitialDisplayRows([]);
    };

    // 모달 컴포넌트에서 호출할 핸들러를 노출한다.
    useImperativeHandle(ref, () => ({
      // 변경된 데이터 또는 표시설정 변경 여부를 검사한다.
      checkValidity: async () => {
        const modifiedRows = grid1.current?.getChange?.();
        const hasChanges = (modifiedRows?.modifiedRowcount ?? 0) > 0;
        if (!hasChanges && !configChanged) {
          return { flag: false, message: "변경된 내용이 없습니다." };
        }
        return { flag: true };
      },
      // 확인 시 저장 로직을 실행한다.
      getSelectedValue: async () => {
        const ok = await onSave();
        if (ok) return "success";
        return { excelUpload: false };
      },
      // 현재 선택행을 반환한다.
      getSelectedRow: () => [grid1.current?.getSelectedRows?.() || []],
    }));

    // 팝업 부모 엘리먼트를 grid popupParent로 연결한다.
    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    // 표시항목 재정렬 후 선택한 행 포커스를 동일 항목으로 복원한다.
    useEffect(() => {
      const focusKey = pendingDisplayFocusKeyRef.current;
      if (!focusKey) return;
      const focusIndex = displayColumns.findIndex(
        (row: any) => String(row.cd_column ?? "") === focusKey
      );
      if (focusIndex >= 0) {
        let retry = 0;
        const tryFocus = () => {
          gridDisplay.current?.selectAndFocusRow?.(focusIndex);
          const selectedRows = gridDisplay.current?.getSelectedRows?.() || [];
          const selectedKey = String(selectedRows?.[0]?.cd_column ?? "");
          if (selectedKey === focusKey) {
            pendingDisplayFocusKeyRef.current = "";
            return;
          }
          retry += 1;
          if (retry < 5) {
            setTimeout(tryFocus, 20);
            return;
          }
          pendingDisplayFocusKeyRef.current = "";
        };
        setTimeout(tryFocus, 0);
      } else {
        pendingDisplayFocusKeyRef.current = "";
      }
    }, [displayColumns]);

    // 화면 초기 진입 시 컬럼/데이터를 준비한다.
    useEffect(() => {
      const fetchForm = async () => {
        try {
          const rtn: any = await getInitPopUpData("SYS_ADDINFO_POP_01", {});
          setColumns(rtn.grid.grid1);
          setTargetColumnsConfig(rtn.grid.grid2 || []);
          setDisplayColumnsConfig(rtn.grid.grid3 || []);
          initializeColumnLists(columnInfos);
          await onSearch();
        } catch (error: any) {
          showDialog("error", error.message);
        }
      };
      fetchForm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo, columnInfos]);

    // 부가항목 설정 목록을 조회한다.
    const onSearch = async () => {
      try {
        const params = {
          id: "sys_addinfo_pop_01_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu: cd_menu ?? "",
            id_target: id_target ?? "",
          },
        };

        setDataSource1([]);
        const data: any = await fnGetDataSet(params);

        // 하단 부가항목(data[0]) 바인딩
        const addInfoRows = Array.isArray(data?.[0]) ? data[0] : [];
        setDataSource1(addInfoRows);

        // 표시방식(data[1]) 바인딩
        const displayInfo = Array.isArray(data?.[1]) ? data[1]?.[0] : undefined;
        const fgDisplay = String(displayInfo?.fg_display ?? "G")
          .trim()
          .toUpperCase();
        setDisplayFg(fgDisplay === "F" ? "F" : "G");
        setHasDisplayConfig(!!displayInfo);

        // 표시항목(data[2]) 바인딩
        const nextDisplay = data[2];
        // 좌측 대상항목은 "전체 컬럼 - 우측 표시항목"으로 계산해 중복 표시를 방지한다.
        const sourceColumns =
          Array.isArray(columnInfos) && columnInfos.length > 0
            ? columnInfos
            : masterColumns;
        const displaySet = new Set(
          nextDisplay.map((row: any) => String(row.cd_column ?? ""))
        );
        const nextTarget = sourceColumns.filter(
          (row: any) => !displaySet.has(String(row.cd_column ?? ""))
        );
        setDisplayColumns(nextDisplay);
        setInitialDisplayRows(Array.isArray(nextDisplay) ? nextDisplay : []);
        setTargetColumns(nextTarget);
        setConfigChanged(false);

        if (addInfoRows.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          grid1.current?.selectAndFocusRow?.(0);
          grid1.current?.resetUpdate?.();
        }
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    // 표시설정 저장 payload를 생성한다.
    const buildDisplaySavePayload = () => {
      const nextHeaderRows = resequenceDisplayColumns(displayColumns).map(
        (row: any) => ({
          cd_firm: userInfo.cd_firm,
          cd_menu: cd_menu ?? "",
          id_target: id_target ?? "",
          no_seq_p: row.no_seq_p,
          cd_column: row.cd_column,
          qt_size: row.qt_size ?? row.qt_colwidth ?? 0,
        })
      );
      const deleteHeaderRows = (initialDisplayRows || []).map((row: any) => ({
        cd_firm: userInfo.cd_firm,
        cd_menu: cd_menu ?? "",
        id_target: id_target ?? "",
        no_seq_p: Number(row?.no_seq_p ?? 0),
      }));
      const addInfoHRow = {
        cd_firm: userInfo.cd_firm,
        cd_menu: cd_menu ?? "",
        id_target: id_target ?? "",
        fg_display: displayFg,
      };

      return {
        sys_addinfoh: {
          insert: hasDisplayConfig ? [] : [addInfoHRow],
          update: hasDisplayConfig ? [addInfoHRow] : [],
          delete: [],
        },
        sys_addinfo_header: {
          insert: nextHeaderRows,
          update: [],
          delete: deleteHeaderRows,
        },
      };
    };

    // 부가항목/표시설정 변경사항을 저장한다.
    const onSave = async () => {
      const changedRows = grid1.current?.getChange?.();
      if ((changedRows?.modifiedRowcount ?? 0) < 1 && !configChanged) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const rowsToValidate = [
        ...(changedRows?.insert ?? []),
        ...(changedRows?.update ?? []),
      ];
      for (const row of rowsToValidate) {
        const tpData = normalizeTpData(row?.tp_data);
        const qtValid = String(row?.dc_qt_valid ?? "").trim();
        const cdClas = String(row?.cd_clas ?? "").trim();
        const rowNo = row?.no_line ?? "";

        if (tpData === "BOOL" && qtValid !== "1") {
          await showDialog(
            "alter",
            `No.${rowNo} 데이터타입이 BOOL이면 자릿수는 1이어야 합니다.`
          );
          return false;
        }
        if (tpData === "DATE" && qtValid !== "8") {
          await showDialog(
            "alter",
            `No.${rowNo} 데이터타입이 DATE이면 자릿수는 8이어야 합니다.`
          );
          return false;
        }
        if (tpData === "CODE") {
          if (qtValid !== "6") {
            await showDialog(
              "alter",
              `No.${rowNo} 데이터타입이 CODE이면 자릿수는 6이어야 합니다.`
            );
            return false;
          }
          if (!cdClas) {
            await showDialog(
              "alter",
              `No.${rowNo} 데이터타입이 CODE이면 코드값(cd_clas)은 필수입니다.`
            );
            return false;
          }
        }
      }

      const saveData: any = {
        sys_addinfo: changedRows,
        ...buildDisplaySavePayload(),
      };

      try {
        const ok = await fnSave("SYS_ADDINFO_SAVE_01", saveData);
        if (ok) {
          grid1.current?.resetUpdate?.();
          return true;
        }
        return false;
      } catch (error: any) {
        await showDialog(
          "error",
          error?.message || "저장 중 오류가 발생했습니다."
        );
        return false;
      }
    };

    // 부가항목 설정 행을 추가한다.
    const onAddRow = () => {
      grid1.current?.addRow?.({});
    };

    // 부가항목 설정 선택행을 삭제한다.
    const onDeleteRow = () => {
      grid1.current?.deleteSelectedRows?.();
    };

    // 부가항목 설정 그리드 상단 버튼 클릭을 처리한다.
    const onGridBtnsClick = (id: string) => {
      if (id === "addRow") onAddRow();
      if (id === "deleteRow") onDeleteRow();
    };

    // 부가항목 설정 행 추가 전 기본값을 구성한다.
    const onBeforeAddRow = (): RowData | boolean => {
      try {
        const allRows: any[] = [];
        grid1.current?.forEachNode?.((node: any) => allRows.push(node.data));
        const maxNoLine =
          allRows.length > 0
            ? Math.max(...allRows.map((row: any) => Number(row.no_line || 0)))
            : 0;

        return {
          cd_firm: userInfo.cd_firm,
          no_line: maxNoLine + 1,
          cd_menu: cd_menu ?? "",
          id_target: id_target ?? "",
          yn_null: "Y",
        };
      } catch {
        return false;
      }
    };

    // tp_data 변경 시 자리수 기본값을 자동 반영한다.
    const onCellValueChanged = (event: any) => {
      if (event?.colId !== "tp_data") return;
      const tpData = normalizeTpData(event?.newValue);
      if (tpData === "BOOL") event?.rowNode?.setDataValue?.("dc_qt_valid", "1");
      else if (tpData === "CODE")
        event?.rowNode?.setDataValue?.("dc_qt_valid", "6");
      else if (tpData === "DATE")
        event?.rowNode?.setDataValue?.("dc_qt_valid", "8");
    };

    // 대상항목에서 선택한 행을 표시항목으로 이동한다(중복 추가 방지).
    const onMoveToDisplay = () => {
      const selectedRows = gridTarget.current?.getSelectedRows?.() || [];
      if (!Array.isArray(selectedRows) || selectedRows.length < 1) return;

      const existingSet = new Set(
        displayColumns.map((row: any) => String(row.cd_column ?? ""))
      );
      const addRows = selectedRows.filter(
        (row: any) => !existingSet.has(String(row.cd_column ?? ""))
      );
      if (addRows.length < 1) return;

      const addSet = new Set(
        addRows.map((row: any) => String(row.cd_column ?? ""))
      );
      const nextDisplay = resequenceDisplayColumns([
        ...displayColumns,
        ...addRows.map((row: any) => ({ ...row, no_seq_p: 0 })),
      ]);
      const nextTarget = masterColumns.filter(
        (row: any) =>
          !addSet.has(String(row.cd_column ?? "")) &&
          !nextDisplay.some(
            (disp: any) =>
              String(disp.cd_column ?? "") === String(row.cd_column ?? "")
          )
      );
      setTargetColumns(nextTarget);
      setDisplayColumns(nextDisplay);
      setConfigChanged(true);
    };

    // 표시항목에서 선택한 행을 대상항목으로 되돌린다.
    const onMoveToTarget = () => {
      const selectedRows = gridDisplay.current?.getSelectedRows?.() || [];
      if (!Array.isArray(selectedRows) || selectedRows.length < 1) return;

      const selectedSet = new Set(
        selectedRows.map((row: any) => String(row.cd_column ?? ""))
      );
      const nextDisplay = resequenceDisplayColumns(
        displayColumns.filter(
          (row: any) => !selectedSet.has(String(row.cd_column ?? ""))
        )
      );
      const nextDisplaySet = new Set(
        nextDisplay.map((row: any) => String(row.cd_column ?? ""))
      );
      const nextTarget = masterColumns.filter(
        (row: any) => !nextDisplaySet.has(String(row.cd_column ?? ""))
      );
      setDisplayColumns(nextDisplay);
      setTargetColumns(nextTarget);
      setConfigChanged(true);
    };

    // 대상항목 행 더블클릭 시 버튼 클릭 로직(onMoveToDisplay)을 재사용한다.
    const onTargetCellDoubleClicked = (event: any) => {
      const rowIndex = Number(event?.rowIndex ?? -1);
      if (rowIndex < 0) return;
      gridTarget.current?.selectAndFocusRow?.(rowIndex);
      onMoveToDisplay();
    };

    // 표시항목 행 더블클릭 시 버튼 클릭 로직(onMoveToTarget)을 재사용한다.
    const onDisplayCellDoubleClicked = (event: any) => {
      const rowIndex = Number(event?.rowIndex ?? -1);
      if (rowIndex < 0) return;
      gridDisplay.current?.selectAndFocusRow?.(rowIndex);
      onMoveToTarget();
    };

    // 표시항목 선택행을 위로 이동한다.
    const onMoveDisplayUp = () => {
      const selectedRows = gridDisplay.current?.getSelectedRows?.() || [];
      if (!Array.isArray(selectedRows) || selectedRows.length !== 1) return;
      const selectedKey = String(selectedRows[0]?.cd_column ?? "");
      const idx = displayColumns.findIndex(
        (row: any) => String(row.cd_column ?? "") === selectedKey
      );
      if (idx <= 0) return;

      const next = [...displayColumns];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      pendingDisplayFocusKeyRef.current = selectedKey;
      setDisplayColumns(resequenceDisplayColumns(next));
      setConfigChanged(true);
    };

    // 표시항목 선택행을 아래로 이동한다.
    const onMoveDisplayDown = () => {
      const selectedRows = gridDisplay.current?.getSelectedRows?.() || [];
      if (!Array.isArray(selectedRows) || selectedRows.length !== 1) return;
      const selectedKey = String(selectedRows[0]?.cd_column ?? "");
      const idx = displayColumns.findIndex(
        (row: any) => String(row.cd_column ?? "") === selectedKey
      );
      if (idx < 0 || idx >= displayColumns.length - 1) return;

      const next = [...displayColumns];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      pendingDisplayFocusKeyRef.current = selectedKey;
      setDisplayColumns(resequenceDisplayColumns(next));
      setConfigChanged(true);
    };

    return (
      <div className="h-full flex flex-col" ref={popupRootRef}>
        <SnResizable
          direction={"height"}
          seq={1}
          cd_menu={splitCdMenu}
          defaultSize={splitDefaultSize}
          size={splitSizes.panel1height}
          onResizeStop={onSplitResizeStop}
          enable={{ bottom: true }}
          minHeight={160}
        >
          <SnTab
            titles={["상단표시항목"]}
            width="100%"
            height="100%"
            body={[
              <div className="h-full border border-gray-300 p-2">
                <div className="flex items-center gap-4 mb-2 mt-1">
                  <SnLabel text="표시방식" type="AfterLabel" />
                  <SnRadioBox
                    defaultValue={displayFg}
                    js_combo={displayFgOptions}
                    onValueChanged={(value: string) => {
                      setDisplayFg(value === "F" ? "F" : "G");
                      setConfigChanged(true);
                    }}
                  />
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 h-[calc(100%-34px)]">
                  <SnGrid
                    ref={gridTarget}
                    columnConfig={targetColumnsConfig}
                    rowData={targetColumns}
                    popupParent={popupParentEl || undefined}
                    etcProps={{
                      isEditable: false,
                      rowSelection: "multiple",
                      title: "대상항목",
                    }}
                    events={{
                      onCellDoubleClicked: onTargetCellDoubleClicked,
                    }}
                  />
                  <div className="flex flex-col justify-center gap-3">
                    <IconButton
                      onClick={onMoveToDisplay}
                      sx={{ width: 36, height: 36, p: 0.5 }}
                    >
                      <ArrowCircleRightTwoToneIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                    <IconButton
                      onClick={onMoveToTarget}
                      sx={{ width: 36, height: 36, p: 0.5 }}
                    >
                      <ArrowCircleLeftTwoToneIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                  </div>
                  <div className="relative h-full">
                    <SnGrid
                      ref={gridDisplay}
                      columnConfig={displayColumnsConfig}
                      rowData={displayColumns}
                      popupParent={popupParentEl || undefined}
                      etcProps={{
                        isEditable: false,
                        rowSelection: "single",
                        title: "표시항목",
                      }}
                      events={{
                        onCellDoubleClicked: onDisplayCellDoubleClicked,
                      }}
                    />
                    <div className="absolute right-2 top-0 z-10 flex items-center gap-2">
                      <IconButton
                        onClick={onMoveDisplayUp}
                        sx={{ width: 36, height: 36, p: 0.5 }}
                      >
                        <ArrowCircleUpTwoToneIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                      <IconButton
                        onClick={onMoveDisplayDown}
                        sx={{ width: 36, height: 36, p: 0.5 }}
                      >
                        <ArrowCircleDownTwoToneIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </div>
                  </div>
                </div>
              </div>,
            ]}
          />
        </SnResizable>

        <div className="flex-1 min-h-0 flex justify-start">
          <SnGrid
            ref={grid1}
            columnConfig={columns}
            rowData={dataSource1}
            popupParent={popupParentEl || undefined}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "부가항목",
            }}
            events={{
              onBeforeAddRow,
              onCellValueChanged,
              onBtnAddClick: onGridBtnsClick,
              onBtnDeleteClick: onGridBtnsClick,
            }}
          />
        </div>
      </div>
    );
  }
);

export default SYS_ADDINFO_POP_01;
